import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { store } from 'components/Store';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import useTranscriptsFeatureEnabled from 'components/Dashboard/hooks/useTranscriptsFeatureEnabled';
import getAppSite from 'utils/getAppSite';
import { AIConnectorModal, wandIcon } from 'components/Settings/Integrations/AIConnector';
import integrationsCondition from 'components/Settings/Integrations/integrationsCondition';
import { WebsiteFeaturePopup, WebsitePopup } from 'components/Settings/WebsiteFeeds/EmbedWebsiteButton';
import RequestCustomSourcePopup from 'components/Dashboard/SidebarElements/RequestCustomSourcePopup';

interface ToolsStoreState {
  activePlan: { enableEmbedWidgets?: boolean; subscriptionStatus?: string };
  keywordsLists: unknown[];
  readyKeywordLists?: boolean;
}

// New on every full page load: history state survives a reload, so a stale tag means the page wasn't opened from here
export const toolsNavigationId = `${Date.now()}`;
const embedUnavailableStatuses = ['SubscribedOverLimit', 'OnFreePlanOverLimit', 'TrialEnded'];
const menuItemClass =
  'w-100 list-action-button py-2 general-button text-left nowrap-item pl-3 specific-right-padding-24 flex-centered';

const ToolsDropdown = () => {
  const { state } = useContext(store) as unknown as { state: ToolsStoreState };
  const { activePlan, keywordsLists, readyKeywordLists } = state;
  const { enableEmbedWidgets, subscriptionStatus } = activePlan;

  const location = useLocation();
  const { inboxSection } = useMainSectionParameter();
  const { transcriptsFeatureEnabled } = useTranscriptsFeatureEnabled();

  const [isOpen, setIsOpen] = useState(false);
  const [showAIConnector, setShowAIConnector] = useState(false);
  const [showEmbedPopup, setShowEmbedPopup] = useState(false);
  const [showEmbedFeaturePopup, setShowEmbedFeaturePopup] = useState(false);
  const [showCustomSourcePopup, setShowCustomSourcePopup] = useState(false);
  const toolsElement = useRef<HTMLDivElement>(null);
  const menuElement = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  useOutsideElement(toolsElement, (target: Element) => {
    if (menuElement.current?.contains(target)) return;
    setIsOpen(false);
  });

  // The menu is portalled to <body> so stacking contexts in the subnav (e.g. the mobile topic picker) can't cover it
  useLayoutEffect(() => {
    if (!isOpen || !toolsElement.current) return;
    const rect = toolsElement.current.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 6, right: document.documentElement.clientWidth - rect.right });
  }, [isOpen]);

  const transcriptsEnabled = inboxSection && getAppSite() !== 'usa' && transcriptsFeatureEnabled;
  const embedFeedEnabled =
    !!readyKeywordLists && !embedUnavailableStatuses.includes(subscriptionStatus ?? '') && keywordsLists.length > 0;
  const customSourceEnabled = getAppSite() === 'global';

  if (!integrationsCondition && !transcriptsEnabled && !embedFeedEnabled && !customSourceEnabled) return null;

  return (
    <>
      <div className='embed-button-container position-relative' ref={toolsElement}>
        <button
          aria-label='Tools'
          aria-expanded={isOpen}
          className='general-button action-state-hover border rounded-pill specific-x-padding-8 specific-left-padding-lg-18 specific-right-padding-lg-18 ml-2 ml-lg-0 main-subtle-text nowrap-item flex-centered justify-content-center'
          style={{ minWidth: '38px', height: '38px' }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className='icon-three-dots d-inline-block-centered centered-1 d-md-none' />
          <span
            className={`dropdown-item-element dropdown-item-element-10 d-none d-md-inline-block ${isOpen ? 'open' : ''}`}
          >
            Tools
          </span>
        </button>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={menuElement}
            className='box-shadow py-2 position-fixed z-extra-max rounded'
            style={menuPosition ?? { visibility: 'hidden' }}
          >
            {integrationsCondition && (
              <button
                className={menuItemClass}
                onClick={() => {
                  setIsOpen(false);
                  setShowAIConnector(true);
                }}
              >
                <div className='icon-general-container text-center specific-right-margin-7 d-inline-block-centered'>
                  <img src={wandIcon} alt='' style={{ width: '16px', transform: 'translateY(-1px)' }} />
                </div>
                <span>Connect to your AI assistant</span>
              </button>
            )}
            {customSourceEnabled && (
              <button
                className={menuItemClass}
                onClick={() => {
                  setIsOpen(false);
                  setShowCustomSourcePopup(true);
                }}
              >
                <div className='icon-general-container text-center specific-right-margin-7'>
                  <span className='icon-add paragraph-p1 d-inline-block-centered centered-5' />
                </div>
                <span>Request custom source</span>
              </button>
            )}
            {transcriptsEnabled && (
              <Link
                to='/monitor-inbox/transcripts/create'
                state={{ returnUrl: `${location.pathname}${location.search}`, toolsNavigationId }}
                className={`${menuItemClass} reset-link text-main-blue reset-link-color`}
                onClick={() => setIsOpen(false)}
              >
                <div className='icon-general-container text-center specific-right-margin-7'>
                  <span className='icon-mic title-h4' />
                </div>
                <span>Create transcript</span>
              </Link>
            )}
            {embedFeedEnabled && (
              <button
                className={menuItemClass}
                onClick={() => {
                  setIsOpen(false);
                  if (enableEmbedWidgets) {
                    setShowEmbedPopup(true);
                  } else {
                    setShowEmbedFeaturePopup(true);
                  }
                }}
              >
                <div className='icon-general-container text-center specific-right-margin-8'>
                  <span
                    className='icon-code-thin paragraph-p5 d-inline-block-centered'
                    style={{ transform: 'translateY(-1px)' }}
                  />
                </div>
                <span>Embed feed</span>
                {!enableEmbedWidgets && <span className='icon-padlock ml-1 paragraph-p4' />}
              </button>
            )}
          </div>,
          document.body
        )}
      <AIConnectorModal isOpen={showAIConnector} setIsOpen={setShowAIConnector} />
      <WebsitePopup showPopUp={showEmbedPopup} setShowPopup={setShowEmbedPopup} />
      <WebsiteFeaturePopup showPopUp={showEmbedFeaturePopup} setShowPopup={setShowEmbedFeaturePopup} />
      <RequestCustomSourcePopup isOpen={showCustomSourcePopup} setIsOpen={setShowCustomSourcePopup} />
    </>
  );
};

export default ToolsDropdown;
